import { canonicalDemandFlow } from './demand';
import type {
  ScenarioDraftInput,
  ScenarioFleetUnitOption,
  ScenarioNodeOption,
  ScenarioProjectSettings,
  ScenarioValidationIssue,
} from './types';

function sameIds(expected: string[], actual: string[]): boolean {
  return (
    expected.length === actual.length &&
    new Set(actual).size === actual.length &&
    [...expected].sort().every((identifier, index) => identifier === [...actual].sort()[index])
  );
}

function demandMatches(
  condition: ScenarioDraftInput['sinks'][number],
): boolean {
  if (
    condition.job_requirement === undefined ||
    condition.job_requirement === null
  ) {
    return true;
  }
  const deliveryPeriod =
    condition.job_requirement.delivery_period_s ?? null;
  const expectedUptime =
    condition.job_requirement.expected_uptime_fraction ?? null;
  if ((deliveryPeriod === null) !== (expectedUptime === null)) {
    return false;
  }
  if (
    deliveryPeriod !== null &&
    expectedUptime !== null &&
    (!Number.isFinite(deliveryPeriod) ||
      deliveryPeriod <= 0 ||
      !Number.isFinite(expectedUptime) ||
      expectedUptime <= 0 ||
      expectedUptime > 1)
  ) {
    return false;
  }

  const canonical = canonicalDemandFlow(condition.job_requirement);
  return (
    canonical !== null &&
    condition.required_flow_m3_s !== null &&
    Math.abs(canonical - condition.required_flow_m3_s) <=
      Math.max(1.0e-15, Math.abs(canonical) * 1.0e-12)
  );
}

export function validateScenarioDraft(
  scenario: ScenarioDraftInput,
  sourceNodes: ScenarioNodeOption[],
  sinkNodes: ScenarioNodeOption[],
  settings: ScenarioProjectSettings,
  fleetUnits: ScenarioFleetUnitOption[] = [],
): ScenarioValidationIssue[] {
  const issues: ScenarioValidationIssue[] = [];
  if (scenario.name.trim() === '') {
    issues.push({
      code: 'scenario_name_missing',
      field: 'name',
      message: 'Enter a scenario name.',
    });
  }
  if (!sameIds(
    sourceNodes.map((node) => node.id),
    scenario.sources.map((condition) => condition.source_node_id),
  )) {
    issues.push({
      code: 'scenario_sources_incomplete',
      field: 'sources',
      message: 'Define every source exactly once.',
    });
  }
  if (!sameIds(
    sinkNodes.map((node) => node.id),
    scenario.sinks.map((condition) => condition.sink_node_id),
  )) {
    issues.push({
      code: 'scenario_sinks_incomplete',
      field: 'sinks',
      message: 'Define every sink exactly once.',
    });
  }
  if (
    scenario.sources.some(
      (condition) =>
        condition.hydraulic_head_m === null ||
        !Number.isFinite(condition.hydraulic_head_m) ||
        condition.maximum_withdrawal_flow_m3_s === null ||
        !Number.isFinite(condition.maximum_withdrawal_flow_m3_s) ||
        condition.maximum_withdrawal_flow_m3_s <= 0,
    )
  ) {
    issues.push({
      code: 'scenario_source_values_missing',
      field: 'sources',
      message: 'Enter hydraulic head and a positive withdrawal limit for every source.',
    });
  }
  if (
    scenario.sinks.some(
      (condition) =>
        !demandMatches(condition) ||
        condition.required_flow_m3_s === null ||
        !Number.isFinite(condition.required_flow_m3_s) ||
        condition.required_flow_m3_s <= 0 ||
        condition.minimum_residual_pressure_gauge_pa === null ||
        !Number.isFinite(condition.minimum_residual_pressure_gauge_pa) ||
        condition.minimum_residual_pressure_gauge_pa < 0,
    )
  ) {
    issues.push({
      code: 'scenario_sink_values_missing',
      field: 'sinks',
      message: 'Enter positive minimum flow and non-negative gauge pressure for every sink.',
    });
  }
  const water = scenario.water;
  if (
    !Number.isFinite(water.temperature_k) ||
    water.temperature_k < 273.15 ||
    water.temperature_k > 373.15 ||
    !Number.isFinite(water.density_kg_m3) ||
    water.density_kg_m3 <= 0 ||
    !Number.isFinite(water.kinematic_viscosity_m2_s) ||
    water.kinematic_viscosity_m2_s <= 0 ||
    !Number.isFinite(water.vapor_pressure_absolute_pa) ||
    water.vapor_pressure_absolute_pa < 0
  ) {
    issues.push({
      code: 'scenario_water_properties_invalid',
      field: 'water',
      message: 'Enter valid water temperature, specific gravity, viscosity, and vapor pressure.',
    });
  }
  const fleetIds = scenario.fleet_settings.map((item) => item.fleet_unit_id);
  const knownFleetIds = new Set(fleetUnits.map((item) => item.id));
  if (
    new Set(fleetIds).size !== fleetIds.length ||
    scenario.fleet_settings.some(
      (item) =>
        (fleetUnits.length > 0 && !knownFleetIds.has(item.fleet_unit_id)) ||
        (item.maximum_engine_speed_rpm !== null &&
          (!Number.isFinite(item.maximum_engine_speed_rpm) ||
            item.maximum_engine_speed_rpm <= 0)) ||
        !Number.isFinite(item.driver_power_derate_fraction) ||
        item.driver_power_derate_fraction < 0 ||
        item.driver_power_derate_fraction >= 1,
    )
  ) {
    issues.push({
      code: 'scenario_fleet_settings_invalid',
      field: 'fleet_settings',
      message: 'Choose each named fleet unit once and enter a valid optional RPM limit and derate.',
    });
  }
  if (
    settings.minimum_profile_pressure_gauge_pa !== null &&
    (!Number.isFinite(settings.minimum_profile_pressure_gauge_pa) ||
      settings.minimum_profile_pressure_gauge_pa < 0)
  ) {
    issues.push({
      code: 'minimum_profile_pressure_invalid',
      field: 'minimum_profile_pressure_gauge_pa',
      message: 'Minimum internal route pressure must be blank or non-negative.',
    });
  }
  if (!settings.safety_acknowledged) {
    issues.push({
      code: 'safety_settings_unacknowledged',
      field: 'safety',
      message: 'Review and acknowledge the project safety margins.',
    });
  }
  if (!settings.terrain_accuracy_acknowledged) {
    issues.push({
      code: 'terrain_warning_unacknowledged',
      field: 'terrain',
      message: 'Review and acknowledge the terrain-provider accuracy warning.',
    });
  }
  return issues;
}

export function validateRequiredScenarioKinds(
  scenarios: ScenarioDraftInput[],
): ScenarioValidationIssue[] {
  const requiredKinds = new Set(
    scenarios.filter((scenario) => scenario.required).map((scenario) => scenario.kind),
  );
  return (['normal', 'peak'] as const)
    .filter((kind) => !requiredKinds.has(kind))
    .map((kind) => ({
      code: 'required_scenario_missing',
      field: 'kind',
      message: `Create a required ${kind} scenario.`,
    }));
}
